import React, { useState } from "react";
import SEO from "../components/SEO";
import {
  Code,
  Smartphone,
  Video,
  Shield,
  Headphones,
  Cloud,
  Router,
  GraduationCap,
  BarChart2,
  Handshake,
  MessageSquare,
  FileEdit,
  ArrowRight,
  MapPin,
  ExternalLink,
  Mail,
  Phone,
  Clock
} from "lucide-react";

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  code: Code,
  smartphone: Smartphone,
  videocam: Video,
  security: Shield,
  support_agent: Headphones,
  cloud: Cloud,
  router: Router,
  school: GraduationCap,
  bar_chart: BarChart2,
  handshake: Handshake,
  chat_bubble: MessageSquare,
  edit_note: FileEdit,
  mail: Mail,
  call: Phone,
  schedule: Clock,
  location_on: MapPin,
  open_in_new: ExternalLink,
};

const ContactScreen: React.FC = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    inquiryType: "",
    otherInquiry: "",
    message: "",
  });
  const [status, setStatus] = useState<
    "idle" | "submitting" | "success" | "error"
  >("idle");

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("submitting");

    try {
      const response = await fetch(
        "/api/contact",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            inquiryType:
              formData.inquiryType === "Other"
                ? `Other — ${formData.otherInquiry}`
                : formData.inquiryType,
            message: formData.message,
          }),
        },
      );

      if (response.ok) {
        setStatus("success");
        setFormData({
          name: "",
          email: "",
          inquiryType: "",
          otherInquiry: "",
          message: "",
        });
      } else {
        setStatus("error");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setStatus("error");
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title="Contact Us | InnovaTech"
        description="Get in touch with InnovaTech. Visit our headquarters at Salmaniyya Plaza, Dutse, Jigawa State or contact our software and IT consulting engineering team."
        keywords="contact innovatech, innovatech address dutse, it company dutse jigawa phone number, tech support jigawa state"
        canonical="/contact"
      >
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "ContactPage",
                "@id": "https://www.innovatech-ng.com/contact#webpage",
                url: "https://www.innovatech-ng.com/contact",
                name: "Contact Us | InnovaTech",
                description:
                  "Get in touch with InnovaTech. Visit our headquarters at Salmaniyya Plaza, Dutse, Jigawa State or contact our software and IT consulting engineering team.",
                breadcrumb: {
                  "@type": "BreadcrumbList",
                  itemListElement: [
                    {
                      "@type": "ListItem",
                      position: 1,
                      name: "Home",
                      item: "https://www.innovatech-ng.com/",
                    },
                    {
                      "@type": "ListItem",
                      position: 2,
                      name: "Contact Us",
                      item: "https://www.innovatech-ng.com/contact",
                    },
                  ],
                },
                mainEntity: {
                  "@id": "https://www.innovatech-ng.com/#organization",
                },
              },
              {
                "@type": "LocalBusiness",
                "@id": "https://www.innovatech-ng.com/#organization",
                name: "InnovaTech Consultancy Limited",
                alternateName: "InnovaTech",
                url: "https://www.innovatech-ng.com",
                logo: "https://www.innovatech-ng.com/logo/logo.png",
                image: "https://www.innovatech-ng.com/logo/logo.png",
                telephone: "+234 813 023 7608",
                email: "inquiry@innovatech-ng.com",
                address: {
                  "@type": "PostalAddress",
                  streetAddress: "No.2 Salmaniyya Plaza, Opp. TCN, Ibrahim Aliyu Bypass",
                  addressLocality: "Dutse",
                  addressRegion: "Jigawa",
                  postalCode: "720101",
                  addressCountry: "NG",
                },
                geo: {
                  "@type": "GeoCoordinates",
                  latitude: 11.6928,
                  longitude: 9.348,
                },
                openingHoursSpecification: [
                  {
                    "@type": "OpeningHoursSpecification",
                    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
                    opens: "08:00",
                    closes: "18:00",
                  },
                ],
              },
            ],
          })}
        </script>
      </SEO>
      <header className="relative w-full h-[600px] flex flex-col justify-end pb-20 overflow-hidden bg-canvas dark:bg-[#0C1B2E]">
        <div className="absolute inset-0 z-0 opacity-50 dark:opacity-20 grayscale mix-blend-multiply dark:mix-blend-overlay">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m12!1m8!1m3!1d3907.0222953829384!2d9.3479818!3d11.6928323!3m2!1i1024!2i768!4f13.1!2m1!1sinnovatech!5e0!3m2!1sen!2sng!4v1766350541190!5m2!1sen!2sng"
            width="100%"
            height="100%"
            title="InnovaTech Headquarters Location Map"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="w-full h-full object-cover grayscale opacity-40 hover:grayscale-0 transition-all duration-700"
          ></iframe>
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-canvas via-canvas/80 to-transparent dark:from-[#0C1B2E] dark:via-[#0C1B2E]/90 z-10 pointer-events-none"></div>
        <div className="relative z-20 px-4 md:px-10 lg:px-40 w-full max-w-7xl mx-auto pointer-events-none">
          <div className="flex flex-col max-w-3xl">
            <span className="text-primary dark:text-red-400 font-bold tracking-widest uppercase text-xs mb-4 flex items-center gap-2">
              <span className="w-8 h-[2px] bg-primary dark:bg-red-400"></span> Contact Us
            </span>
            <h1 className="text-text-main dark:text-white text-4xl md:text-6xl font-bold tracking-tight leading-[1.1] mb-6">
              Contact InnovaTech <br className="hidden md:block" />
              <span className="text-text-sub dark:text-slate-400 text-2xl md:text-3xl font-normal block mt-2">
                Let's build your next digital solution together.
              </span>
            </h1>
            <p className="text-text-sub dark:text-slate-400 text-lg md:text-xl max-w-xl leading-relaxed">
              Our team is ready to answer your questions about our ICT
              solutions. Reach out to discuss enterprise integration, custom
              platforms, or strategic partnerships.
            </p>
          </div>
        </div>
      </header>

      <main className="relative z-30 px-4 md:px-10 lg:px-40 pb-24 -mt-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Form */}
          <div className="lg:col-span-7 flex flex-col">
            <div className="bg-surface dark:bg-[#1E293B] border border-border-subtle dark:border-slate-700 rounded-xl p-8 shadow-sm h-full flex flex-col">
              <h3 className="text-xl font-bold text-text-main dark:text-white mb-6">
                Send us a message
              </h3>
              <form
                className="flex flex-col gap-6 flex-1"
                onSubmit={handleSubmit}
              >
                {status === "success" && (
                  <div className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 p-4 rounded-lg text-sm mb-4">
                    Message sent successfully! We'll get back to you soon.
                  </div>
                )}
                {status === "error" && (
                  <div className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 p-4 rounded-lg text-sm mb-4">
                    Something went wrong. Please try again later.
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <label className="flex flex-col gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wider text-text-sub dark:text-slate-400">
                      Full Name
                    </span>
                    <input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      autoComplete="name"
                      className="w-full bg-canvas dark:bg-slate-800/50 border border-border-subtle dark:border-slate-700 rounded-lg px-4 py-3 text-sm text-text-main dark:text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all placeholder:text-slate-400"
                      placeholder="John Doe"
                      type="text"
                      required
                    />
                  </label>
                  <label className="flex flex-col gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wider text-text-sub dark:text-slate-400">
                      Company Email
                    </span>
                    <input
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      autoComplete="email"
                      className="w-full bg-canvas dark:bg-slate-800/50 border border-border-subtle dark:border-slate-700 rounded-lg px-4 py-3 text-sm text-text-main dark:text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all placeholder:text-slate-400"
                      placeholder="name@company.com"
                      type="email"
                      required
                    />
                  </label>
                </div>
                <div className="flex flex-col gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wider text-text-sub dark:text-slate-400">
                    Inquiry Type
                  </span>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {[
                      { label: "Web Development", icon: "code" },
                      { label: "Mobile App", icon: "smartphone" },
                      { label: "CCTV & Surveillance", icon: "videocam" },
                      { label: "Cybersecurity", icon: "security" },
                      { label: "IT Consultancy", icon: "support_agent" },
                      { label: "Cloud & DevOps", icon: "cloud" },
                      { label: "VoIP & Networking", icon: "router" },
                      { label: "Institute / Training", icon: "school" },
                      { label: "Data Analytics", icon: "bar_chart" },
                      { label: "Partnership / Sales", icon: "handshake" },
                      { label: "General Inquiry", icon: "chat_bubble" },
                      { label: "Other", icon: "edit_note" },
                    ].map(({ label, icon }) => (
                      <button
                        key={label}
                        type="button"
                        onClick={() =>
                          setFormData((f) => ({
                            ...f,
                            inquiryType: label,
                            otherInquiry: "",
                          }))
                        }
                        className={`flex flex-col items-start gap-1.5 p-3 rounded-lg border text-left transition-all ${
                          formData.inquiryType === label
                            ? "border-primary dark:border-red-500/50 bg-primary/5 dark:bg-red-500/10 text-primary dark:text-red-400"
                            : "border-border-subtle dark:border-slate-700 bg-canvas dark:bg-slate-800/50 text-text-sub dark:text-slate-400 hover:border-primary/50 hover:text-text-main dark:hover:text-white"
                        }`}
                      >
                        {(() => {
                          const IconComponent = ICON_MAP[icon];
                          return IconComponent ? <IconComponent className="w-[18px] h-[18px]" /> : null;
                        })()}
                        <span className="text-[11px] font-semibold leading-tight">
                          {label}
                        </span>
                      </button>
                    ))}
                  </div>

                  {/* Custom input when Other is selected */}
                  {formData.inquiryType === "Other" && (
                    <input
                      type="text"
                      name="otherInquiry"
                      value={formData.otherInquiry}
                      onChange={handleChange}
                      required
                      placeholder="Describe your inquiry type..."
                      className="mt-1 w-full bg-canvas dark:bg-slate-800/50 border border-primary rounded-lg px-4 py-3 text-sm text-text-main dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all placeholder:text-slate-400"
                    />
                  )}

                  {/* Validation hint */}
                  {!formData.inquiryType && (
                    <p className="text-[11px] text-text-sub dark:text-slate-500 mt-0.5">
                      Please select an inquiry type above.
                    </p>
                  )}
                </div>
                <label className="flex flex-col gap-2 flex-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-text-sub dark:text-slate-400">
                    Message
                  </span>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full h-full bg-canvas dark:bg-slate-800/50 border border-border-subtle dark:border-slate-700 rounded-lg px-4 py-3 text-sm text-text-main dark:text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all placeholder:text-slate-400 resize-none min-h-[150px]"
                    placeholder={
                      formData.inquiryType === "Web Development"
                        ? "Tell us about your web project — goals, tech stack, timeline..."
                        : formData.inquiryType === "Mobile App"
                          ? "Describe your app idea — platform (iOS/Android), features, audience..."
                          : formData.inquiryType === "CCTV & Surveillance"
                            ? "Where is the site? How many cameras? Indoor/outdoor? Any existing system?"
                            : formData.inquiryType === "Cybersecurity"
                              ? "Describe your infrastructure and what you’d like assessed or secured..."
                              : formData.inquiryType === "IT Consultancy"
                                ? "What technology challenge are you facing? What’s the business goal?"
                                : formData.inquiryType === "Cloud & DevOps"
                                  ? "What stack are you on? What do you want to migrate, automate, or scale?"
                                  : formData.inquiryType === "VoIP & Networking"
                                    ? "How many users/lines? Any existing PBX? Office or remote setup?"
                                    : formData.inquiryType ===
                                        "Institute / Training"
                                      ? "What course interests you? Individual or group enrollment?"
                                      : formData.inquiryType ===
                                          "Data Analytics"
                                        ? "What data do you have? What business questions do you need answered?"
                                        : formData.inquiryType ===
                                            "Partnership / Sales"
                                          ? "Tell us about your organization and what kind of partnership you envision..."
                                          : formData.inquiryType ===
                                              "General Inquiry"
                                            ? "Ask us anything — we’ll get back to you shortly."
                                            : "Provide details about your inquiry..."
                    }
                    required
                  ></textarea>
                </label>
                <div className="pt-2 mt-auto">
                  <button
                    disabled={
                      status === "submitting" ||
                      !formData.inquiryType ||
                      (formData.inquiryType === "Other" &&
                        !formData.otherInquiry.trim())
                    }
                    className="group w-full md:w-auto bg-primary text-white font-bold py-3.5 px-8 rounded-lg shadow-lg shadow-primary/20 hover:bg-red-600 hover:shadow-xl hover:shadow-primary/30 active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    type="submit"
                  >
                    {status === "submitting" ? (
                      <>
                        <span className="size-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <span>Send Message</span>
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Info Sidebar */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            <div className="grid grid-cols-1 gap-4">
              <div className="group bg-surface dark:bg-[#1E293B] border border-border-subtle dark:border-slate-700 rounded-xl p-6 transition-all hover:border-primary/50">
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-canvas dark:bg-slate-800 rounded-lg text-text-main dark:text-white group-hover:text-primary transition-colors">
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-text-main dark:text-white mb-1">
                      Headquarters
                    </h4>
                    <p className="text-sm text-text-sub dark:text-slate-400 leading-relaxed mb-3">
                      No.2 Salmaniyya Plaza, Opp, TCN,
                      <br />
                      Ibrahim Aliyu Bypass, Dutse, Jigawa State
                    </p>
                    <a
                      className="inline-flex items-center text-xs font-semibold text-primary dark:text-red-400 hover:underline"
                      href="https://maps.google.com"
                      target="_blank"
                      rel="noreferrer"
                    >
                      View on Map{" "}
                      <ExternalLink className="w-[14px] h-[14px] ml-1" />
                    </a>
                  </div>
                </div>
                <div className="mt-4 h-48 w-full rounded-lg overflow-hidden relative bg-slate-100 dark:bg-slate-800 border border-white/10">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m12!1m8!1m3!1d3907.0222953829384!2d9.3479818!3d11.6928323!3m2!1i1024!2i768!4f13.1!2m1!1sinnovatech!5e0!3m2!1sen!2sng!4v1766350541190!5m2!1sen!2sng"
                    width="100%"
                    height="100%"
                    title="InnovaTech Head Office Location Map"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  ></iframe>
                </div>
              </div>

              {/* Contact Info */}
              <div className="bg-surface dark:bg-[#1E293B] border border-border-subtle dark:border-slate-700 rounded-xl p-6">
                <h4 className="text-sm font-bold uppercase tracking-wider text-text-sub dark:text-slate-500 mb-4 border-b border-border-subtle dark:border-slate-700 pb-2">
                  Direct Lines
                </h4>
                <div className="flex flex-col gap-6">
                  {[
                    {
                      icon: "mail",
                      label: "Email Address",
                      value: "info@innovatech-ng.com",
                    },
                    {
                      icon: "call",
                      label: "Phone Number",
                      value: "+234 813 023 7608",
                    },
                  ].map((line, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-4 group cursor-pointer"
                    >
                      <div className="p-2 bg-canvas dark:bg-slate-800 rounded-lg text-text-main dark:text-white group-hover:text-primary transition-colors">
                        {(() => {
                          const IconComponent = ICON_MAP[line.icon];
                          return IconComponent ? <IconComponent className="w-6 h-6" /> : null;
                        })()}
                      </div>
                      <div>
                        <p className="text-xs font-medium text-text-sub dark:text-slate-400">
                          {line.label}
                        </p>
                        <p className="text-sm font-bold text-text-main dark:text-white group-hover:text-primary transition-colors">
                          {line.value}
                        </p>
                      </div>
                    </div>
                  ))}

                  {/* Business Hours */}
                  <div className="flex items-center gap-4 group">
                    <div className="p-2 bg-canvas dark:bg-slate-800 rounded-lg text-text-main dark:text-white group-hover:text-primary transition-colors">
                      <Clock className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-text-sub dark:text-slate-400">
                        Business Hours
                      </p>
                      <p className="text-sm font-bold text-text-main dark:text-white">
                        Mon-Fri: 9AM - 6PM
                      </p>
                      <p className="text-xs text-text-sub dark:text-slate-500">
                        Sat-Sun: Closed
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Socials */}
              <div className="bg-surface dark:bg-[#1E293B] border border-border-subtle dark:border-slate-700 rounded-xl p-6">
                <h4 className="text-sm font-bold uppercase tracking-wider text-text-sub dark:text-slate-500 mb-4 border-b border-border-subtle dark:border-slate-700 pb-2">
                  Connect With Us
                </h4>
                <div className="flex gap-2 flex-wrap">
                  {[
                    {
                      icon: (
                        <svg
                          viewBox="0 0 24 24"
                          className="w-5 h-5 fill-current"
                        >
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"></path>
                        </svg>
                      ),
                      url: "https://x.com/innovatech_ng",
                    },
                    {
                      icon: (
                        <svg
                          viewBox="0 0 24 24"
                          className="w-5 h-5 fill-current"
                        >
                          <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
                        </svg>
                      ),
                      url: "https://youtube.com/innovatech-ng",
                    },
                    {
                      icon: (
                        <svg
                          viewBox="0 0 24 24"
                          className="w-5 h-5 fill-current"
                        >
                          <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.35-1.17 1.09-1.07 1.93.03.5.25 1.02.63 1.34 1.15 1.02 3.12 1.1 4.35.08 1.15-1.07 1.14-2.93 1.1-4.63v-8.72c-1.42-.01-2.69-.01-4.1-.02z" />
                        </svg>
                      ),
                      url: "https://tiktok.com/@innovatech-ng",
                    },
                    {
                      icon: (
                        <svg
                          viewBox="0 0 24 24"
                          className="w-5 h-5 fill-current"
                        >
                          <path d="M7.8,2H16.2C19.4,2 22,4.6 22,7.8V16.2A5.8,5.8 0 0,1 16.2,22H7.8C4.6,22 2,19.4 2,16.2V7.8A5.8,5.8 0 0,1 7.8,2M7.6,4A3.6,3.6 0 0,0 4,7.6V16.4C4,18.39 5.61,20 7.6,20H16.4A3.6,3.6 0 0,0 20,16.4V7.6C20,5.61 18.39,4 16.4,4H7.6M17.25,5.5A1.25,1.25 0 0,1 18.5,6.75A1.25,1.25 0 0,1 17.25,8A1.25,1.25 0 0,1 16,6.75A1.25,1.25 0 0,1 17.25,5.5M12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9Z" />
                        </svg>
                      ),
                      url: "https://instagram.com/innovatech-ng",
                    },
                    {
                      icon: (
                        <svg
                          viewBox="0 0 24 24"
                          className="w-5 h-5 fill-current"
                        >
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                        </svg>
                      ),
                      url: "https://linkedin.com/company/innovatech-ng",
                    },
                  ].map((social, i) => (
                    <a
                      key={i}
                      href={social.url}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`Visit our ${social.url.includes("x.com") ? "X (Twitter)" : social.url.includes("youtube") ? "YouTube" : social.url.includes("tiktok") ? "TikTok" : social.url.includes("instagram") ? "Instagram" : "LinkedIn"} page`}
                      className="flex h-10 w-10 items-center justify-center rounded-lg bg-canvas dark:bg-slate-800 text-text-sub dark:text-slate-400 hover:bg-primary hover:text-white transition-all"
                    >
                      {social.icon}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>


    </div>
  );
};

export default ContactScreen;
